import { useQuery } from "@apollo/client";
import SelectableListPopover from "@arrive/selectable-list-popover";
import { GET_ALL_COUNTRIES } from "api/queries";
import { COUNTRY_SUGGESTIONS_AVAILABLE } from "constants/loadConstants/loadConstants";
import React, { useEffect, useRef, useState } from "react";
import cx from 'classnames';
import Drawer from '@arrive/ui-icons/svg/ic_drawer.svg';

import styles from './CountrySuggestionsDropdown.scss'
import TextEntry from "@arrive/text-entry";
import SVGIcon from "@arrive/svg-icon";

export interface Country {
    CountryId: number;
    Name: string;
    PhoneCode: string;
    Alpha2Code: string;
}

export interface CountryQueryType {
    getAllCountries: Country[];
}

export interface CountryCodeDropdownProps {
    value: number;
    label?: string;
    onChange: (selectedId: number) => void,
    selectableListPopoverClasses?: {
        anchorClass?: string;
        contentContainerClass?: string;
    },
    renderContent?: (country: Country) => React.ReactNode,
    required?: boolean,
    disabled?: boolean,
    matchAnchorWidth?: boolean,
}

export const emptyCountry: Country = {
  CountryId: null,
  Name: '',
  PhoneCode: '',
  Alpha2Code: ''
}

const renderCountryContent = (country: Country) => {
  return (
    <div className={styles.countrySuggestionsDropdown__option}>
      <span>{country.Name}</span>
      <span>+{country.PhoneCode}</span>
    </div>
  );
}

export const CountryCodeDropdown = ({
  value,
  onChange,
  required = false,
  label = 'Country Code',
  selectableListPopoverClasses,
  matchAnchorWidth = false,
  disabled = false
}: CountryCodeDropdownProps) => {
  const [showTextPopover, setShowTextPopover] = useState(false);
  const [availableCountries, setAvailableCountries] = useState<Country[]>([]);
  const [selectedOption, setSelectedOption] = useState<Country>(emptyCountry);
  const textEntryRef = useRef();
  const isAvailableCountry = (country: Country) => Object.values(COUNTRY_SUGGESTIONS_AVAILABLE).includes(country.CountryId);
  const filterAvailableCountries = (allCountries: Country[]) => allCountries.filter(isAvailableCountry);
  const options = availableCountries.map(country => ({ id: country.CountryId, content: renderCountryContent(country) }));
  const emptyCallback = () => ({});
  const onValidSelectionChange = ([id]: number[]) => id ? onChange(id) : null

  useQuery<CountryQueryType>(GET_ALL_COUNTRIES, {
    onCompleted: response => setAvailableCountries(filterAvailableCountries(response.getAllCountries))
  });

  useEffect(() => {
    const country = availableCountries.find(el => el.CountryId === value);
    setSelectedOption(country ? country : emptyCountry);
  }, [availableCountries, value]);

  return (
    <SelectableListPopover
      showPopover={showTextPopover}
      setShowPopover={setShowTextPopover}
      classes={{
        anchorClass: cx('countrySuggestionsDropdownAnchor', selectableListPopoverClasses?.anchorClass),
        contentContainerClass: cx(styles.countrySuggestionsDropdownOptions, selectableListPopoverClasses?.contentContainerClass)
      }}
      options={options}
      selectedOptionIds={[value]}
      onChange={onValidSelectionChange}
      focusLockProps={{ shards: [textEntryRef] }}
      placement='bottom'
      matchAnchorWidth={matchAnchorWidth}
      closeOnSingleSelect
    >
      <TextEntry
        label={label}
        showPopover={showTextPopover}
        ref={textEntryRef}
        tabSelect={() => setShowTextPopover(!showTextPopover)}
        onFocus={() => setShowTextPopover(true)}
        onChange={emptyCallback}
        onSelectionChange={emptyCallback}
        value={selectedOption.CountryId ? `${selectedOption.Alpha2Code} +${selectedOption.PhoneCode}` : ''}
        required={required}
        disabled={disabled}
        suffix={(
          disabled
            ? null
            : <div className={styles.countrySuggestionsDropdown__iconContainer} onClick={() => setShowTextPopover(!showTextPopover)}>
              <SVGIcon className={styles.countrySuggestionsDropdown__icon} svgRef={Drawer} />
            </div>
        )}
      />
    </SelectableListPopover>
  );
}
